import React from 'react';
import _ from 'lodash';
import clsx from 'clsx';
import { t } from 'i18next';
import { FieldArray, useFormikContext } from 'formik';
import { Icon } from '@brighterion/br-ui';
import { handleKeyEvent, handleOnClickEvent } from '@brighterion/utils/general';
import { RuleGroupConditionsSchema } from 'Components/RuleGroupConditionsForm/ruleGroupConditionsSchema';
import Conditions from 'Components/Conditions';

import styles from './RuleGroupConditions.css';

const RuleGroupConditions = () => {
    const { values, errors, isSubmitting, initialValues } = useFormikContext<RuleGroupConditionsSchema>();
    const isVisible = true;
    const createCondition = initialValues.conditions[0];

    const formLabels = [
        '',
        t('ruleGroupConditions.form.parameter.label'),
        t('ruleGroupConditions.form.operator.label'),
        t('ruleGroupConditions.form.values.label'),
        '',
        t('ruleGroupConditions.form.readonly.label')
    ];

    const isConditionsValid = (conditions: RuleGroupConditionsSchema['conditions']) => {
        /** This will return -1 if the array of conditions values are NOT empty */
        const result = conditions.findIndex((condition) => condition.values === '');
        return Boolean(result === -1);
    };

    const conditionsErrors = () => Boolean(typeof errors.conditions !== 'undefined');

    return (
        <section className={styles.section}>
            <div className={styles.sectionDescription}>{t('ruleGroupConditions.form.description')}</div>
            <div className={styles.sectionHeader}>{t('Conditions')}</div>
            <div className={styles.formGroup}>
                <div className={styles.conditionContainer}>
                    {formLabels.map((label) => (
                        <div key={_.uniqueId(label)} className={styles.conditionColumn}>
                            {label}
                        </div>
                    ))}
                </div>

                <FieldArray
                    name='conditions'
                    render={(conditionsArrayHelper) => (
                        <>
                            {values.conditions.map((condition, index) => {
                                const handleRemoveCondition = (i: number) => {
                                    if (i === 0) {
                                        conditionsArrayHelper.replace(i, condition);
                                    } else {
                                        conditionsArrayHelper.remove(i);
                                    }
                                };

                                return (
                                    // eslint-disable-next-line react/no-array-index-key
                                    <div key={index} className={styles.conditionRow}>
                                        <Conditions index={index} />
                                        {isVisible && (
                                            <Icon
                                                aria-disabled={index === 0}
                                                className={clsx(
                                                    styles.removeBtn,
                                                    isSubmitting || (index === 0 && styles.removeBtnDisabled)
                                                )}
                                                name='feature-remove'
                                                size='md'
                                                onClick={handleOnClickEvent(() => {
                                                    handleRemoveCondition(index);
                                                })}
                                                onKeyDown={handleKeyEvent(() => {
                                                    handleRemoveCondition(index);
                                                })}
                                            />
                                        )}
                                    </div>
                                );
                            })}
                            {isVisible && (
                                <button
                                    type='button'
                                    tabIndex={0}
                                    aria-disabled={!isConditionsValid(values.conditions) || conditionsErrors()}
                                    disabled={!isConditionsValid(values.conditions) || conditionsErrors()}
                                    className={styles.addConclusionButton}
                                    onKeyDown={handleKeyEvent(() => conditionsArrayHelper.push(createCondition))}
                                    onClick={handleOnClickEvent(() => conditionsArrayHelper.push(createCondition))}
                                >
                                    <Icon name='add-file' size='md' />
                                    <span>{t('ruleGroupConditions.form.actions.create')}</span>
                                </button>
                            )}
                        </>
                    )}
                />
            </div>
        </section>
    );
};

export default RuleGroupConditions;
